/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";

import * as S from "./DealTermsCrossLicenseTableContainer.style";
import { createArrayToCountValue, useCheckWindowSize } from "../../../shared";
import DealTermArrow from "../DealTermArrow/DealTermArrow";
import ParagraphSectionLayout from "../ParagraphSectionLayout/ParagraphSectionLayout";

type RightsScopeValue = {
  drugInfo: {
    drug: React.ReactNode;
    mainUsage: React.ReactNode;
  }[];
  exclusivity: React.ReactNode;
  territory: React.ReactNode;
};

type FinancialTermsValue = {
  upfrontPayment: React.ReactNode;
  milestonePayment: React.ReactNode;
  otherPayment: React.ReactNode;
  totalDealValue: React.ReactNode;
  royalty: React.ReactNode;
};
type Props = {
  isMobileViewer?: boolean;
  licensingInfoData: {
    partnerA: {
      licensingName: React.ReactNode;
      responsibilityAndRights: React.ReactNode;
    };
    partnerB: {
      licensingName: React.ReactNode;
      responsibilityAndRights: React.ReactNode;
    };
  };

  financialTermsData: {
    partnerA: FinancialTermsValue;
    partnerB: FinancialTermsValue;
  };
  rightScopeData: {
    partnerA: RightsScopeValue[];
    partnerB: RightsScopeValue[];
  };
};

const checkTextEmpty = (text: React.ReactNode) => {
  if (text === undefined) return true;
  if (text === "") return true;
  if ((text as any)?.props?.children?.at(1).props.item.text === "") return true;
  if ((text as any)?.props?.item?.text === "") return true;
  return false;
};
const checkEmptyRowValue = (
  partnerAValue: React.ReactNode,
  partnerBValue: React.ReactNode,
) => {
  if (!!checkTextEmpty(partnerAValue) && !!checkTextEmpty(partnerBValue))
    return true;

  return false;
};

const checkEmptyEveryRightsScopeTableValue = (
  rightsScopeTableValueInfo?: RightsScopeValue,
) => {
  if (rightsScopeTableValueInfo === undefined) return true;
  return Object.values(rightsScopeTableValueInfo || {}).every((value) => {
    if (Array.isArray(value) === true) {
      return value.length === 0;
    }

    return (
      (value as any)?.props?.item?.text === "" ||
      value === undefined ||
      (value as any)?.props?.children?.at(1).props.item.text === ""
    );
  });
};

const checkEmptyEveryFinancialTermsTableValue = (
  financialTermsValueInfo?: FinancialTermsValue,
) => {
  if (financialTermsValueInfo === undefined) return true;
  return Object.values(financialTermsValueInfo).every((value) => {
    return (value as any)?.props?.item?.text === "" || value === undefined;
  });
};

const DealTermsCrossLicenseTableContainer = ({
  licensingInfoData,
  financialTermsData,
  rightScopeData,
  isMobileViewer = false,
}: Props) => {
  const { isMobileScreen: isMobileSize } = useCheckWindowSize();
  const isMobileScreen = isMobileSize && isMobileViewer;

  const isPartnerAAllRightsScopeTableValueEmpty = rightScopeData.partnerA
    .map((tableData) => checkEmptyEveryRightsScopeTableValue(tableData))
    .every((isEmptyData) => isEmptyData === true);
  const isPartnerBAllRightsScopeTableValueEmpty = rightScopeData.partnerB
    .map((tableData) => checkEmptyEveryRightsScopeTableValue(tableData))
    .every((isEmptyData) => isEmptyData === true);

  const isPartnerAFinancialTermsTableValueEmpty =
    checkEmptyEveryFinancialTermsTableValue(financialTermsData.partnerA);
  const isPartnerBFinancialTermsTableValueEmpty =
    checkEmptyEveryFinancialTermsTableValue(financialTermsData.partnerB);

  const isEmptyRightsScopeAndFinancialTerms =
    isPartnerAAllRightsScopeTableValueEmpty &&
    isPartnerBAllRightsScopeTableValueEmpty &&
    isPartnerAFinancialTermsTableValueEmpty &&
    isPartnerBFinancialTermsTableValueEmpty;

  const maxRightsScopeTableLength = Math.max(
    rightScopeData.partnerA.length,
    rightScopeData.partnerB.length,
  );
  const rightsScopeEmptyArray = createArrayToCountValue(
    maxRightsScopeTableLength,
  );
  const calculateDrugInfoMaxData = (
    partnerADrugData: {
      drug: React.ReactNode;
      mainUsage: React.ReactNode;
    }[],
    partnerBDrugData: {
      drug: React.ReactNode;
      mainUsage: React.ReactNode;
    }[],
  ) => {
    return Math.max(partnerADrugData.length, partnerBDrugData.length);
  };

  const checkTableGap = () => {
    if (isMobileScreen) return 16;
    return 32;
  };
  const checkTableWithBackgroundGap = () => {
    if (isMobileScreen) return 16;
    return 64;
  };

  const MOBILE_TITLE_DRUG_INFO = "Asset(s) & Main Usage";

  const EMPTY_TABLE_BY_DIRECTION = {
    left:
      isPartnerAFinancialTermsTableValueEmpty &&
      isPartnerAAllRightsScopeTableValueEmpty,
    right:
      isPartnerBFinancialTermsTableValueEmpty &&
      isPartnerBAllRightsScopeTableValueEmpty,
  };

  const isNoneArrow = !(
    !EMPTY_TABLE_BY_DIRECTION.left && !EMPTY_TABLE_BY_DIRECTION.right
  );

  return (
    <ParagraphSectionLayout>
      <S.TableWrapper $isMobileViewer={isMobileViewer}>
        <S.Table>
          <S.TableBody>
            <S.TableRow>
              <S.BodyShell width={`calc(50% - ${checkTableGap() / 2}px)`}>
                <S.TitleContentsShell $isRadius>
                  Licensing Partner A
                </S.TitleContentsShell>
              </S.BodyShell>
              <S.BodyShell rowSpan={4} width={`${checkTableGap()}px`} />
              <S.BodyShell width={`calc(50% - ${checkTableGap() / 2}px)`}>
                <S.TitleContentsShell $isRadius>
                  Licensing Partner B
                </S.TitleContentsShell>
              </S.BodyShell>
            </S.TableRow>

            <S.TableRow>
              <S.BodyShell width={`calc(50% - ${checkTableGap() / 2}px)`}>
                <S.ValueContentsShell
                  $isBottomBorder={checkEmptyRowValue(
                    licensingInfoData.partnerA.responsibilityAndRights,
                    licensingInfoData.partnerB.responsibilityAndRights,
                  )}
                >
                  {licensingInfoData.partnerA.licensingName}
                </S.ValueContentsShell>
              </S.BodyShell>
              <S.BodyShell width={`calc(50% - ${checkTableGap() / 2}px)`}>
                <S.ValueContentsShell
                  $isBottomBorder={checkEmptyRowValue(
                    licensingInfoData.partnerA.responsibilityAndRights,
                    licensingInfoData.partnerB.responsibilityAndRights,
                  )}
                >
                  {licensingInfoData.partnerB.licensingName}
                </S.ValueContentsShell>
              </S.BodyShell>
            </S.TableRow>

            <S.TableRow>
              <S.BodyShell width={`calc(50% - ${checkTableGap() / 2}px)`}>
                <S.TitleContentsShell
                  $isNoneDisplay={checkEmptyRowValue(
                    licensingInfoData.partnerA.responsibilityAndRights,
                    licensingInfoData.partnerB.responsibilityAndRights,
                  )}
                >
                  Responsibility & Rights
                </S.TitleContentsShell>
              </S.BodyShell>
              <S.BodyShell width={`calc(50% - ${checkTableGap() / 2}px)`}>
                <S.TitleContentsShell
                  $isNoneDisplay={checkEmptyRowValue(
                    licensingInfoData.partnerA.responsibilityAndRights,
                    licensingInfoData.partnerB.responsibilityAndRights,
                  )}
                >
                  Responsibility & Rights
                </S.TitleContentsShell>
              </S.BodyShell>
            </S.TableRow>

            <S.TableRow>
              <S.BodyShell width={`calc(50% - ${checkTableGap() / 2}px)`}>
                <S.ValueContentsShell
                  $isBottomBorder
                  $isNoneDisplay={checkEmptyRowValue(
                    licensingInfoData.partnerA.responsibilityAndRights,
                    licensingInfoData.partnerB.responsibilityAndRights,
                  )}
                >
                  {licensingInfoData.partnerA.responsibilityAndRights}
                </S.ValueContentsShell>
              </S.BodyShell>
              <S.BodyShell width={`calc(50% - ${checkTableGap() / 2}px)`}>
                <S.ValueContentsShell
                  $isBottomBorder
                  $isNoneDisplay={checkEmptyRowValue(
                    licensingInfoData.partnerA.responsibilityAndRights,
                    licensingInfoData.partnerB.responsibilityAndRights,
                  )}
                >
                  {licensingInfoData.partnerB.responsibilityAndRights}
                </S.ValueContentsShell>
              </S.BodyShell>
            </S.TableRow>
          </S.TableBody>
        </S.Table>
      </S.TableWrapper>
      <S.TableGroupWrapper
        $isMobileViewer={isMobileViewer}
        $isNoneDisplay={isEmptyRightsScopeAndFinancialTerms}
        $dataInfoByDirection={EMPTY_TABLE_BY_DIRECTION}
      >
        <S.TableWrapper $isMobileViewer={isMobileViewer}>
          <S.ArrowContainer
            $isMobileViewer={isMobileViewer}
            $isNoneDisplay={isNoneArrow}
          >
            <DealTermArrow arrowType="both" />
          </S.ArrowContainer>
          {(isPartnerAAllRightsScopeTableValueEmpty === false ||
            isPartnerBAllRightsScopeTableValueEmpty === false) && (
            <>
              <S.Table>
                <S.TableBody>
                  <S.TableRow>
                    <S.BodyShell
                      width={`calc(50% - ${
                        checkTableWithBackgroundGap() / 2
                      }px)`}
                    >
                      <S.TableSectionTitle
                        $isNoneDisplay={isPartnerAAllRightsScopeTableValueEmpty}
                      >
                        Rights Scope
                      </S.TableSectionTitle>
                    </S.BodyShell>
                    <S.BodyShell
                      rowSpan={100}
                      width={`${checkTableWithBackgroundGap()}px`}
                    />
                    <S.BodyShell
                      width={`calc(50% - ${
                        checkTableWithBackgroundGap() / 2
                      }px)`}
                    >
                      <S.TableSectionTitle
                        $isNoneDisplay={isPartnerBAllRightsScopeTableValueEmpty}
                      >
                        Rights Scope
                      </S.TableSectionTitle>
                    </S.BodyShell>
                  </S.TableRow>
                </S.TableBody>
              </S.Table>
              {rightsScopeEmptyArray.map((tableIndex) => {
                const maxDrugInfoRowLength = calculateDrugInfoMaxData(
                  rightScopeData.partnerA.at(tableIndex)?.drugInfo || [],
                  rightScopeData.partnerB.at(tableIndex)?.drugInfo || [],
                );
                const drugInfoEmptyArray =
                  createArrayToCountValue(maxDrugInfoRowLength);

                const isEmptyPartnerARightsScope =
                  rightScopeData.partnerA.at(tableIndex) === undefined ||
                  !!checkEmptyEveryRightsScopeTableValue(
                    rightScopeData.partnerA.at(tableIndex),
                  );
                const isEmptyPartnerBRightsScope =
                  rightScopeData.partnerB.at(tableIndex) === undefined ||
                  !!checkEmptyEveryRightsScopeTableValue(
                    rightScopeData.partnerB.at(tableIndex),
                  );

                return (
                  <S.Table key={`rights-scope-table-${tableIndex}`}>
                    <S.TableBody>
                      {isMobileScreen && (
                        <S.TableRow>
                          <S.BodyShell
                            width={`calc(50% - ${
                              checkTableWithBackgroundGap() / 2
                            }px)`}
                            colSpan={2}
                          >
                            <S.TitleContentsShell
                              $isRadius
                              $isNoneDisplay={isEmptyPartnerARightsScope}
                            >
                              {MOBILE_TITLE_DRUG_INFO}
                            </S.TitleContentsShell>
                          </S.BodyShell>

                          <S.BodyShell
                            rowSpan={100}
                            width={`${checkTableWithBackgroundGap()}px`}
                          />
                          <S.BodyShell
                            width={`calc(50% - ${
                              checkTableWithBackgroundGap() / 2
                            }px)`}
                            colSpan={2}
                          >
                            <S.TitleContentsShell
                              $isRadius
                              $isNoneDisplay={isEmptyPartnerBRightsScope}
                            >
                              {MOBILE_TITLE_DRUG_INFO}
                            </S.TitleContentsShell>
                          </S.BodyShell>
                        </S.TableRow>
                      )}
                      {!isMobileScreen && (
                        <S.TableRow>
                          <S.BodyShell
                            width={`calc(50% - ${
                              checkTableWithBackgroundGap() / 2
                            }px)`}
                          >
                            <S.TitleContentsShell
                              $isRadius
                              $isNoneDisplay={isEmptyPartnerARightsScope}
                              style={{
                                borderTopRightRadius: 0,
                                borderRight: "0.5px solid #E5E7EB",
                              }}
                            >
                              Asset(s)
                            </S.TitleContentsShell>
                          </S.BodyShell>

                          <S.BodyShell
                            width={`calc(50% - ${
                              checkTableWithBackgroundGap() / 2
                            }px)`}
                          >
                            <S.TitleContentsShell
                              $isRadius
                              $isNoneDisplay={isEmptyPartnerARightsScope}
                              style={{
                                borderTopLeftRadius: 0,
                                borderLeft: "0.5px solid #E5E7EB",
                              }}
                            >
                              Main Usage
                            </S.TitleContentsShell>
                          </S.BodyShell>
                          <S.BodyShell
                            rowSpan={100}
                            width={`${checkTableWithBackgroundGap()}px`}
                          />
                          <S.BodyShell
                            width={`calc(50% - ${
                              checkTableWithBackgroundGap() / 2
                            }px)`}
                          >
                            <S.TitleContentsShell
                              $isRadius
                              $isNoneDisplay={isEmptyPartnerBRightsScope}
                              style={{
                                borderTopRightRadius: 0,
                                borderRight: "0.5px solid #E5E7EB",
                              }}
                            >
                              Asset(s)
                            </S.TitleContentsShell>
                          </S.BodyShell>

                          <S.BodyShell
                            width={`calc(50% - ${
                              checkTableWithBackgroundGap() / 2
                            }px)`}
                          >
                            <S.TitleContentsShell
                              $isRadius
                              $isNoneDisplay={isEmptyPartnerBRightsScope}
                              style={{
                                borderTopLeftRadius: 0,
                                borderLeft: "0.5px solid #E5E7EB",
                              }}
                            >
                              Main Usage
                            </S.TitleContentsShell>
                          </S.BodyShell>
                        </S.TableRow>
                      )}

                      {drugInfoEmptyArray.map((drugInfoIndex) => {
                        const isLastRowOfThisTable =
                          checkEmptyRowValue(
                            rightScopeData.partnerA.at(tableIndex)?.territory,
                            rightScopeData.partnerB.at(tableIndex)?.territory,
                          ) &&
                          checkEmptyRowValue(
                            rightScopeData.partnerA.at(tableIndex)?.exclusivity,
                            rightScopeData.partnerB.at(tableIndex)?.exclusivity,
                          ) &&
                          maxDrugInfoRowLength === drugInfoIndex + 1;

                        return (
                          <S.TableRow>
                            <S.BodyShell
                              width={`calc(50% - ${checkTableGap() / 2}px)`}
                              colSpan={isMobileScreen ? 2 : 1}
                              $isNoneDisplay={
                                isEmptyPartnerARightsScope ||
                                (checkEmptyRowValue(
                                  rightScopeData.partnerA
                                    .at(tableIndex)
                                    ?.drugInfo.at(drugInfoIndex)?.drug,
                                  rightScopeData.partnerA
                                    .at(tableIndex)
                                    ?.drugInfo.at(drugInfoIndex)?.mainUsage,
                                ) &&
                                  checkEmptyRowValue(
                                    rightScopeData.partnerB
                                      .at(tableIndex)
                                      ?.drugInfo.at(drugInfoIndex)?.drug,
                                    rightScopeData.partnerB
                                      .at(tableIndex)
                                      ?.drugInfo.at(drugInfoIndex)?.mainUsage,
                                  ))
                              }
                            >
                              <S.ValueContentsShell
                                $isNoneDisplay={
                                  isEmptyPartnerARightsScope ||
                                  (checkEmptyRowValue(
                                    rightScopeData.partnerA
                                      .at(tableIndex)
                                      ?.drugInfo.at(drugInfoIndex)?.drug,
                                    rightScopeData.partnerA
                                      .at(tableIndex)
                                      ?.drugInfo.at(drugInfoIndex)?.mainUsage,
                                  ) &&
                                    checkEmptyRowValue(
                                      rightScopeData.partnerB
                                        .at(tableIndex)
                                        ?.drugInfo.at(drugInfoIndex)?.drug,
                                      rightScopeData.partnerB
                                        .at(tableIndex)
                                        ?.drugInfo.at(drugInfoIndex)?.mainUsage,
                                    ))
                                }
                                style={{
                                  borderRight: "0.5px solid #E5E7EB",
                                  borderBottom:
                                    maxDrugInfoRowLength !==
                                      drugInfoIndex + 1 || isLastRowOfThisTable
                                      ? "1px solid #E5E7EB"
                                      : "none",
                                  borderBottomLeftRadius: isLastRowOfThisTable
                                    ? "0.4rem"
                                    : "0",
                                  borderBottomRightRadius: isLastRowOfThisTable
                                    ? isMobileScreen
                                      ? "0.4rem"
                                      : "0"
                                    : "0",
                                }}
                              >
                                {!isMobileScreen &&
                                  rightScopeData.partnerA
                                    .at(tableIndex)
                                    ?.drugInfo.at(drugInfoIndex)?.drug}
                                {isMobileScreen && (
                                  <>
                                    <S.MobileDrugText>
                                      {
                                        rightScopeData.partnerA
                                          .at(tableIndex)
                                          ?.drugInfo.at(drugInfoIndex)?.drug
                                      }
                                    </S.MobileDrugText>
                                    {!checkTextEmpty(
                                      rightScopeData.partnerA
                                        .at(tableIndex)
                                        ?.drugInfo.at(drugInfoIndex)?.mainUsage,
                                    ) && (
                                      <S.MobileMainUsageText>
                                        {
                                          rightScopeData.partnerA
                                            .at(tableIndex)
                                            ?.drugInfo.at(drugInfoIndex)
                                            ?.mainUsage
                                        }
                                      </S.MobileMainUsageText>
                                    )}
                                  </>
                                )}
                              </S.ValueContentsShell>
                            </S.BodyShell>
                            {!isMobileScreen && (
                              <S.BodyShell
                                width={`calc(50% - ${checkTableGap() / 2}px)`}
                                $isNoneDisplay={
                                  isEmptyPartnerARightsScope ||
                                  (checkEmptyRowValue(
                                    rightScopeData.partnerA
                                      .at(tableIndex)
                                      ?.drugInfo.at(drugInfoIndex)?.drug,
                                    rightScopeData.partnerA
                                      .at(tableIndex)
                                      ?.drugInfo.at(drugInfoIndex)?.mainUsage,
                                  ) &&
                                    checkEmptyRowValue(
                                      rightScopeData.partnerB
                                        .at(tableIndex)
                                        ?.drugInfo.at(drugInfoIndex)?.drug,
                                      rightScopeData.partnerB
                                        .at(tableIndex)
                                        ?.drugInfo.at(drugInfoIndex)?.mainUsage,
                                    ))
                                }
                              >
                                <S.ValueContentsShell
                                  $isNoneDisplay={
                                    isEmptyPartnerARightsScope ||
                                    (checkEmptyRowValue(
                                      rightScopeData.partnerA
                                        .at(tableIndex)
                                        ?.drugInfo.at(drugInfoIndex)?.drug,
                                      rightScopeData.partnerA
                                        .at(tableIndex)
                                        ?.drugInfo.at(drugInfoIndex)?.mainUsage,
                                    ) &&
                                      checkEmptyRowValue(
                                        rightScopeData.partnerB
                                          .at(tableIndex)
                                          ?.drugInfo.at(drugInfoIndex)?.drug,
                                        rightScopeData.partnerB
                                          .at(tableIndex)
                                          ?.drugInfo.at(drugInfoIndex)
                                          ?.mainUsage,
                                      ))
                                  }
                                  style={{
                                    borderLeft: "0.5px solid #E5E7EB",
                                    borderBottom:
                                      maxDrugInfoRowLength !==
                                        drugInfoIndex + 1 ||
                                      isLastRowOfThisTable
                                        ? "1px solid #E5E7EB"
                                        : "none",
                                    borderBottomRightRadius:
                                      isLastRowOfThisTable ? "0.4rem" : "0",
                                    borderBottomLeftRadius: isLastRowOfThisTable
                                      ? isMobileScreen
                                        ? "0.4rem"
                                        : "0"
                                      : "0",
                                  }}
                                >
                                  {
                                    rightScopeData.partnerA
                                      .at(tableIndex)
                                      ?.drugInfo.at(drugInfoIndex)?.mainUsage
                                  }
                                </S.ValueContentsShell>
                              </S.BodyShell>
                            )}
                            <S.BodyShell
                              width={`calc(50% - ${checkTableGap() / 2}px)`}
                              colSpan={isMobileScreen ? 2 : 1}
                              $isNoneDisplay={
                                isEmptyPartnerBRightsScope ||
                                (checkEmptyRowValue(
                                  rightScopeData.partnerA
                                    .at(tableIndex)
                                    ?.drugInfo.at(drugInfoIndex)?.drug,
                                  rightScopeData.partnerA
                                    .at(tableIndex)
                                    ?.drugInfo.at(drugInfoIndex)?.mainUsage,
                                ) &&
                                  checkEmptyRowValue(
                                    rightScopeData.partnerB
                                      .at(tableIndex)
                                      ?.drugInfo.at(drugInfoIndex)?.drug,
                                    rightScopeData.partnerB
                                      .at(tableIndex)
                                      ?.drugInfo.at(drugInfoIndex)?.mainUsage,
                                  ))
                              }
                            >
                              <S.ValueContentsShell
                                $isNoneDisplay={
                                  isEmptyPartnerBRightsScope ||
                                  (checkEmptyRowValue(
                                    rightScopeData.partnerA
                                      .at(tableIndex)
                                      ?.drugInfo.at(drugInfoIndex)?.drug,
                                    rightScopeData.partnerA
                                      .at(tableIndex)
                                      ?.drugInfo.at(drugInfoIndex)?.mainUsage,
                                  ) &&
                                    checkEmptyRowValue(
                                      rightScopeData.partnerB
                                        .at(tableIndex)
                                        ?.drugInfo.at(drugInfoIndex)?.drug,
                                      rightScopeData.partnerB
                                        .at(tableIndex)
                                        ?.drugInfo.at(drugInfoIndex)?.mainUsage,
                                    ))
                                }
                                style={{
                                  borderRight: "0.5px solid #E5E7EB",
                                  borderBottom:
                                    maxDrugInfoRowLength !==
                                      drugInfoIndex + 1 || isLastRowOfThisTable
                                      ? "1px solid #E5E7EB"
                                      : "none",
                                  borderBottomLeftRadius: isLastRowOfThisTable
                                    ? "0.4rem"
                                    : "0",
                                  borderBottomRightRadius: isLastRowOfThisTable
                                    ? isMobileScreen
                                      ? "0.4rem"
                                      : "0"
                                    : "0",
                                }}
                              >
                                {!isMobileScreen &&
                                  rightScopeData.partnerB
                                    .at(tableIndex)
                                    ?.drugInfo.at(drugInfoIndex)?.drug}
                                {isMobileScreen && (
                                  <>
                                    <S.MobileDrugText>
                                      {
                                        rightScopeData.partnerB
                                          .at(tableIndex)
                                          ?.drugInfo.at(drugInfoIndex)?.drug
                                      }
                                    </S.MobileDrugText>
                                    {!checkTextEmpty(
                                      rightScopeData.partnerB
                                        .at(tableIndex)
                                        ?.drugInfo.at(drugInfoIndex)?.mainUsage,
                                    ) && (
                                      <S.MobileMainUsageText>
                                        {
                                          rightScopeData.partnerB
                                            .at(tableIndex)
                                            ?.drugInfo.at(drugInfoIndex)
                                            ?.mainUsage
                                        }
                                      </S.MobileMainUsageText>
                                    )}
                                  </>
                                )}
                              </S.ValueContentsShell>
                            </S.BodyShell>
                            {!isMobileScreen && (
                              <S.BodyShell
                                width={`calc(50% - ${checkTableGap() / 2}px)`}
                                $isNoneDisplay={
                                  isEmptyPartnerBRightsScope ||
                                  (checkEmptyRowValue(
                                    rightScopeData.partnerA
                                      .at(tableIndex)
                                      ?.drugInfo.at(drugInfoIndex)?.drug,
                                    rightScopeData.partnerA
                                      .at(tableIndex)
                                      ?.drugInfo.at(drugInfoIndex)?.mainUsage,
                                  ) &&
                                    checkEmptyRowValue(
                                      rightScopeData.partnerB
                                        .at(tableIndex)
                                        ?.drugInfo.at(drugInfoIndex)?.drug,
                                      rightScopeData.partnerB
                                        .at(tableIndex)
                                        ?.drugInfo.at(drugInfoIndex)?.mainUsage,
                                    ))
                                }
                              >
                                <S.ValueContentsShell
                                  $isNoneDisplay={
                                    isEmptyPartnerBRightsScope ||
                                    (checkEmptyRowValue(
                                      rightScopeData.partnerA
                                        .at(tableIndex)
                                        ?.drugInfo.at(drugInfoIndex)?.drug,
                                      rightScopeData.partnerA
                                        .at(tableIndex)
                                        ?.drugInfo.at(drugInfoIndex)?.mainUsage,
                                    ) &&
                                      checkEmptyRowValue(
                                        rightScopeData.partnerB
                                          .at(tableIndex)
                                          ?.drugInfo.at(drugInfoIndex)?.drug,
                                        rightScopeData.partnerB
                                          .at(tableIndex)
                                          ?.drugInfo.at(drugInfoIndex)
                                          ?.mainUsage,
                                      ))
                                  }
                                  style={{
                                    borderLeft: "0.5px solid #E5E7EB",
                                    borderBottom:
                                      maxDrugInfoRowLength !==
                                        drugInfoIndex + 1 ||
                                      isLastRowOfThisTable
                                        ? "1px solid #E5E7EB"
                                        : "none",
                                    borderBottomRightRadius:
                                      isLastRowOfThisTable ? "0.4rem" : "0",
                                    borderBottomLeftRadius: isLastRowOfThisTable
                                      ? isMobileScreen
                                        ? "0.4rem"
                                        : "0"
                                      : "0",
                                  }}
                                >
                                  {
                                    rightScopeData.partnerB
                                      .at(tableIndex)
                                      ?.drugInfo.at(drugInfoIndex)?.mainUsage
                                  }
                                </S.ValueContentsShell>
                              </S.BodyShell>
                            )}
                          </S.TableRow>
                        );
                      })}
                      <S.TableRow>
                        <S.BodyShell
                          width={`calc(50% - ${
                            checkTableWithBackgroundGap() / 2
                          }px)`}
                          colSpan={2}
                          $isNoneDisplay={
                            isEmptyPartnerARightsScope ||
                            checkEmptyRowValue(
                              rightScopeData.partnerA.at(tableIndex)
                                ?.exclusivity,
                              rightScopeData.partnerB.at(tableIndex)
                                ?.exclusivity,
                            )
                          }
                        >
                          <S.TitleContentsShell
                            $isNoneDisplay={
                              isEmptyPartnerARightsScope ||
                              checkEmptyRowValue(
                                rightScopeData.partnerA.at(tableIndex)
                                  ?.exclusivity,
                                rightScopeData.partnerB.at(tableIndex)
                                  ?.exclusivity,
                              )
                            }
                          >
                            Exclusivity
                          </S.TitleContentsShell>
                        </S.BodyShell>

                        <S.BodyShell
                          width={`calc(50% - ${
                            checkTableWithBackgroundGap() / 2
                          }px)`}
                          colSpan={2}
                          $isNoneDisplay={
                            isEmptyPartnerBRightsScope ||
                            checkEmptyRowValue(
                              rightScopeData.partnerA.at(tableIndex)
                                ?.exclusivity,
                              rightScopeData.partnerB.at(tableIndex)
                                ?.exclusivity,
                            )
                          }
                        >
                          <S.TitleContentsShell
                            $isNoneDisplay={
                              isEmptyPartnerBRightsScope ||
                              checkEmptyRowValue(
                                rightScopeData.partnerA.at(tableIndex)
                                  ?.exclusivity,
                                rightScopeData.partnerB.at(tableIndex)
                                  ?.exclusivity,
                              )
                            }
                          >
                            Exclusivity
                          </S.TitleContentsShell>
                        </S.BodyShell>
                      </S.TableRow>

                      <S.TableRow>
                        <S.BodyShell
                          width={`calc(50% - ${checkTableGap() / 2}px)`}
                          colSpan={2}
                          $isNoneDisplay={
                            isEmptyPartnerARightsScope ||
                            checkEmptyRowValue(
                              rightScopeData.partnerA.at(tableIndex)
                                ?.exclusivity,
                              rightScopeData.partnerB.at(tableIndex)
                                ?.exclusivity,
                            )
                          }
                        >
                          <S.ValueContentsShell
                            $isNoneDisplay={
                              isEmptyPartnerARightsScope ||
                              checkEmptyRowValue(
                                rightScopeData.partnerA.at(tableIndex)
                                  ?.exclusivity,
                                rightScopeData.partnerB.at(tableIndex)
                                  ?.exclusivity,
                              )
                            }
                            $isBottomBorder={checkEmptyRowValue(
                              rightScopeData.partnerA.at(tableIndex)?.territory,
                              rightScopeData.partnerB.at(tableIndex)?.territory,
                            )}
                          >
                            {
                              rightScopeData.partnerA.at(tableIndex)
                                ?.exclusivity
                            }
                          </S.ValueContentsShell>
                        </S.BodyShell>
                        <S.BodyShell
                          width={`calc(50% - ${checkTableGap() / 2}px)`}
                          colSpan={2}
                          $isNoneDisplay={
                            isEmptyPartnerBRightsScope ||
                            checkEmptyRowValue(
                              rightScopeData.partnerA.at(tableIndex)
                                ?.exclusivity,
                              rightScopeData.partnerB.at(tableIndex)
                                ?.exclusivity,
                            )
                          }
                        >
                          <S.ValueContentsShell
                            $isNoneDisplay={
                              isEmptyPartnerBRightsScope ||
                              checkEmptyRowValue(
                                rightScopeData.partnerA.at(tableIndex)
                                  ?.exclusivity,
                                rightScopeData.partnerB.at(tableIndex)
                                  ?.exclusivity,
                              )
                            }
                            $isBottomBorder={checkEmptyRowValue(
                              rightScopeData.partnerA.at(tableIndex)?.territory,
                              rightScopeData.partnerB.at(tableIndex)?.territory,
                            )}
                          >
                            {
                              rightScopeData.partnerB.at(tableIndex)
                                ?.exclusivity
                            }
                          </S.ValueContentsShell>
                        </S.BodyShell>
                      </S.TableRow>

                      <S.TableRow>
                        <S.BodyShell
                          width={`calc(50% - ${checkTableGap() / 2}px)`}
                          colSpan={2}
                          $isNoneDisplay={
                            isEmptyPartnerARightsScope ||
                            checkEmptyRowValue(
                              rightScopeData.partnerA.at(tableIndex)?.territory,
                              rightScopeData.partnerB.at(tableIndex)?.territory,
                            )
                          }
                        >
                          <S.TitleContentsShell
                            $isNoneDisplay={
                              isEmptyPartnerARightsScope ||
                              checkEmptyRowValue(
                                rightScopeData.partnerA.at(tableIndex)
                                  ?.territory,
                                rightScopeData.partnerB.at(tableIndex)
                                  ?.territory,
                              )
                            }
                          >
                            Territory
                          </S.TitleContentsShell>
                        </S.BodyShell>
                        <S.BodyShell
                          width={`calc(50% - ${checkTableGap() / 2}px)`}
                          colSpan={2}
                          $isNoneDisplay={
                            isEmptyPartnerBRightsScope ||
                            checkEmptyRowValue(
                              rightScopeData.partnerA.at(tableIndex)?.territory,
                              rightScopeData.partnerB.at(tableIndex)?.territory,
                            )
                          }
                        >
                          <S.TitleContentsShell
                            $isNoneDisplay={
                              isEmptyPartnerBRightsScope ||
                              checkEmptyRowValue(
                                rightScopeData.partnerA.at(tableIndex)
                                  ?.territory,
                                rightScopeData.partnerB.at(tableIndex)
                                  ?.territory,
                              )
                            }
                          >
                            Territory
                          </S.TitleContentsShell>
                        </S.BodyShell>
                      </S.TableRow>

                      <S.TableRow>
                        <S.BodyShell
                          width={`calc(50% - ${checkTableGap() / 2}px)`}
                          colSpan={2}
                          $isNoneDisplay={
                            isEmptyPartnerARightsScope ||
                            checkEmptyRowValue(
                              rightScopeData.partnerA.at(tableIndex)?.territory,
                              rightScopeData.partnerB.at(tableIndex)?.territory,
                            )
                          }
                        >
                          <S.ValueContentsShell
                            $isBottomBorder
                            $isNoneDisplay={
                              isEmptyPartnerARightsScope ||
                              checkEmptyRowValue(
                                rightScopeData.partnerA.at(tableIndex)
                                  ?.territory,
                                rightScopeData.partnerB.at(tableIndex)
                                  ?.territory,
                              )
                            }
                          >
                            {rightScopeData.partnerA.at(tableIndex)?.territory}
                          </S.ValueContentsShell>
                        </S.BodyShell>
                        <S.BodyShell
                          width={`calc(50% - ${checkTableGap() / 2}px)`}
                          colSpan={2}
                          $isNoneDisplay={
                            isEmptyPartnerBRightsScope ||
                            checkEmptyRowValue(
                              rightScopeData.partnerA.at(tableIndex)?.territory,
                              rightScopeData.partnerB.at(tableIndex)?.territory,
                            )
                          }
                        >
                          <S.ValueContentsShell
                            $isBottomBorder
                            $isNoneDisplay={
                              isEmptyPartnerBRightsScope ||
                              checkEmptyRowValue(
                                rightScopeData.partnerA.at(tableIndex)
                                  ?.territory,
                                rightScopeData.partnerB.at(tableIndex)
                                  ?.territory,
                              )
                            }
                          >
                            {rightScopeData.partnerB.at(tableIndex)?.territory}
                          </S.ValueContentsShell>
                        </S.BodyShell>
                      </S.TableRow>
                    </S.TableBody>
                  </S.Table>
                );
              })}
            </>
          )}
        </S.TableWrapper>
        <S.TableWrapper $isMobileViewer={isMobileViewer}>
          <S.Table>
            <S.TableBody>
              <S.TableRow>
                <S.BodyShell
                  width={`calc(50% - ${checkTableWithBackgroundGap() / 2}px)`}
                >
                  <S.TableSectionTitle
                    $isNoneDisplay={isPartnerAFinancialTermsTableValueEmpty}
                  >
                    Financial Terms
                  </S.TableSectionTitle>
                </S.BodyShell>
                <S.BodyShell
                  rowSpan={100}
                  width={`${checkTableWithBackgroundGap()}px`}
                />
                <S.BodyShell
                  width={`calc(50% - ${checkTableWithBackgroundGap() / 2}px)`}
                >
                  <S.TableSectionTitle
                    $isNoneDisplay={isPartnerBFinancialTermsTableValueEmpty}
                  >
                    Financial Terms
                  </S.TableSectionTitle>
                </S.BodyShell>
              </S.TableRow>
            </S.TableBody>
          </S.Table>
          <S.Table>
            <S.TableBody>
              <S.TableRow>
                <S.BodyShell
                  width={`calc(50% - ${checkTableWithBackgroundGap() / 2}px)`}
                >
                  <S.TitleContentsShell
                    $isRadius
                    $isNoneDisplay={
                      checkEmptyRowValue(
                        financialTermsData.partnerA.upfrontPayment,
                        financialTermsData.partnerB.upfrontPayment,
                      ) || isPartnerAFinancialTermsTableValueEmpty
                    }
                  >
                    Upfront
                  </S.TitleContentsShell>
                </S.BodyShell>
                <S.BodyShell
                  rowSpan={100}
                  width={`${checkTableWithBackgroundGap()}px`}
                />
                <S.BodyShell
                  width={`calc(50% - ${checkTableWithBackgroundGap() / 2}px)`}
                >
                  <S.TitleContentsShell
                    $isRadius
                    $isNoneDisplay={
                      checkEmptyRowValue(
                        financialTermsData.partnerA.upfrontPayment,
                        financialTermsData.partnerB.upfrontPayment,
                      ) || isPartnerBFinancialTermsTableValueEmpty
                    }
                  >
                    Upfront
                  </S.TitleContentsShell>
                </S.BodyShell>
              </S.TableRow>

              <S.TableRow>
                <S.BodyShell width={`calc(50% - ${checkTableGap() / 2}px)`}>
                  <S.ValueContentsShell
                    $isNoneDisplay={
                      checkEmptyRowValue(
                        financialTermsData.partnerA.upfrontPayment,
                        financialTermsData.partnerB.upfrontPayment,
                      ) || isPartnerAFinancialTermsTableValueEmpty
                    }
                    $isBottomBorder={
                      checkEmptyRowValue(
                        financialTermsData.partnerA.milestonePayment,
                        financialTermsData.partnerB.milestonePayment,
                      ) &&
                      checkEmptyRowValue(
                        financialTermsData.partnerA.otherPayment,
                        financialTermsData.partnerB.otherPayment,
                      ) &&
                      checkEmptyRowValue(
                        financialTermsData.partnerA.totalDealValue,
                        financialTermsData.partnerB.totalDealValue,
                      ) &&
                      checkEmptyRowValue(
                        financialTermsData.partnerA.royalty,
                        financialTermsData.partnerB.royalty,
                      )
                    }
                  >
                    {financialTermsData.partnerA.upfrontPayment}
                  </S.ValueContentsShell>
                </S.BodyShell>
                <S.BodyShell width={`calc(50% - ${checkTableGap() / 2}px)`}>
                  <S.ValueContentsShell
                    $isNoneDisplay={
                      checkEmptyRowValue(
                        financialTermsData.partnerA.upfrontPayment,
                        financialTermsData.partnerB.upfrontPayment,
                      ) || isPartnerBFinancialTermsTableValueEmpty
                    }
                    $isBottomBorder={
                      checkEmptyRowValue(
                        financialTermsData.partnerA.milestonePayment,
                        financialTermsData.partnerB.milestonePayment,
                      ) &&
                      checkEmptyRowValue(
                        financialTermsData.partnerA.otherPayment,
                        financialTermsData.partnerB.otherPayment,
                      ) &&
                      checkEmptyRowValue(
                        financialTermsData.partnerA.totalDealValue,
                        financialTermsData.partnerB.totalDealValue,
                      ) &&
                      checkEmptyRowValue(
                        financialTermsData.partnerA.royalty,
                        financialTermsData.partnerB.royalty,
                      )
                    }
                  >
                    {financialTermsData.partnerB.upfrontPayment}
                  </S.ValueContentsShell>
                </S.BodyShell>
              </S.TableRow>

              <S.TableRow>
                <S.BodyShell width={`calc(50% - ${checkTableGap() / 2}px)`}>
                  <S.TitleContentsShell
                    $isNoneDisplay={
                      checkEmptyRowValue(
                        financialTermsData.partnerA.milestonePayment,
                        financialTermsData.partnerB.milestonePayment,
                      ) || isPartnerAFinancialTermsTableValueEmpty
                    }
                  >
                    Milestones
                  </S.TitleContentsShell>
                </S.BodyShell>
                <S.BodyShell width={`calc(50% - ${checkTableGap() / 2}px)`}>
                  <S.TitleContentsShell
                    $isNoneDisplay={
                      checkEmptyRowValue(
                        financialTermsData.partnerA.milestonePayment,
                        financialTermsData.partnerB.milestonePayment,
                      ) || isPartnerBFinancialTermsTableValueEmpty
                    }
                  >
                    Milestones
                  </S.TitleContentsShell>
                </S.BodyShell>
              </S.TableRow>

              <S.TableRow>
                <S.BodyShell width={`calc(50% - ${checkTableGap() / 2}px)`}>
                  <S.ValueContentsShell
                    $isNoneDisplay={
                      checkEmptyRowValue(
                        financialTermsData.partnerA.milestonePayment,
                        financialTermsData.partnerB.milestonePayment,
                      ) || isPartnerAFinancialTermsTableValueEmpty
                    }
                    $isBottomBorder={
                      checkEmptyRowValue(
                        financialTermsData.partnerA.otherPayment,
                        financialTermsData.partnerB.otherPayment,
                      ) &&
                      checkEmptyRowValue(
                        financialTermsData.partnerA.totalDealValue,
                        financialTermsData.partnerB.totalDealValue,
                      ) &&
                      checkEmptyRowValue(
                        financialTermsData.partnerA.royalty,
                        financialTermsData.partnerB.royalty,
                      )
                    }
                  >
                    {financialTermsData.partnerA.milestonePayment}
                  </S.ValueContentsShell>
                </S.BodyShell>
                <S.BodyShell width={`calc(50% - ${checkTableGap() / 2}px)`}>
                  <S.ValueContentsShell
                    $isNoneDisplay={
                      checkEmptyRowValue(
                        financialTermsData.partnerA.milestonePayment,
                        financialTermsData.partnerB.milestonePayment,
                      ) || isPartnerBFinancialTermsTableValueEmpty
                    }
                    $isBottomBorder={
                      checkEmptyRowValue(
                        financialTermsData.partnerA.otherPayment,
                        financialTermsData.partnerB.otherPayment,
                      ) &&
                      checkEmptyRowValue(
                        financialTermsData.partnerA.totalDealValue,
                        financialTermsData.partnerB.totalDealValue,
                      ) &&
                      checkEmptyRowValue(
                        financialTermsData.partnerA.royalty,
                        financialTermsData.partnerB.royalty,
                      )
                    }
                  >
                    {financialTermsData.partnerB.milestonePayment}
                  </S.ValueContentsShell>
                </S.BodyShell>
              </S.TableRow>

              <S.TableRow>
                <S.BodyShell width={`calc(50% - ${checkTableGap() / 2}px)`}>
                  <S.TitleContentsShell
                    $isNoneDisplay={
                      checkEmptyRowValue(
                        financialTermsData.partnerA.otherPayment,
                        financialTermsData.partnerB.otherPayment,
                      ) || isPartnerAFinancialTermsTableValueEmpty
                    }
                  >
                    Other Payment
                  </S.TitleContentsShell>
                </S.BodyShell>
                <S.BodyShell width={`calc(50% - ${checkTableGap() / 2}px)`}>
                  <S.TitleContentsShell
                    $isNoneDisplay={
                      checkEmptyRowValue(
                        financialTermsData.partnerA.otherPayment,
                        financialTermsData.partnerB.otherPayment,
                      ) || isPartnerBFinancialTermsTableValueEmpty
                    }
                  >
                    Other Payment
                  </S.TitleContentsShell>
                </S.BodyShell>
              </S.TableRow>

              <S.TableRow>
                <S.BodyShell width={`calc(50% - ${checkTableGap() / 2}px)`}>
                  <S.ValueContentsShell
                    $isNoneDisplay={
                      checkEmptyRowValue(
                        financialTermsData.partnerA.otherPayment,
                        financialTermsData.partnerB.otherPayment,
                      ) || isPartnerAFinancialTermsTableValueEmpty
                    }
                    $isBottomBorder={
                      checkEmptyRowValue(
                        financialTermsData.partnerA.totalDealValue,
                        financialTermsData.partnerB.totalDealValue,
                      ) &&
                      checkEmptyRowValue(
                        financialTermsData.partnerA.royalty,
                        financialTermsData.partnerB.royalty,
                      )
                    }
                  >
                    {financialTermsData.partnerA.otherPayment}
                  </S.ValueContentsShell>
                </S.BodyShell>
                <S.BodyShell width={`calc(50% - ${checkTableGap() / 2}px)`}>
                  <S.ValueContentsShell
                    $isNoneDisplay={
                      checkEmptyRowValue(
                        financialTermsData.partnerA.otherPayment,
                        financialTermsData.partnerB.otherPayment,
                      ) || isPartnerBFinancialTermsTableValueEmpty
                    }
                    $isBottomBorder={
                      checkEmptyRowValue(
                        financialTermsData.partnerA.totalDealValue,
                        financialTermsData.partnerB.totalDealValue,
                      ) &&
                      checkEmptyRowValue(
                        financialTermsData.partnerA.royalty,
                        financialTermsData.partnerB.royalty,
                      )
                    }
                  >
                    {financialTermsData.partnerB.otherPayment}
                  </S.ValueContentsShell>
                </S.BodyShell>
              </S.TableRow>

              <S.TableRow>
                <S.BodyShell width={`calc(50% - ${checkTableGap() / 2}px)`}>
                  <S.TitleContentsShell
                    $isNoneDisplay={
                      checkEmptyRowValue(
                        financialTermsData.partnerA.totalDealValue,
                        financialTermsData.partnerB.totalDealValue,
                      ) || isPartnerAFinancialTermsTableValueEmpty
                    }
                  >
                    Total Payment
                  </S.TitleContentsShell>
                </S.BodyShell>
                <S.BodyShell width={`calc(50% - ${checkTableGap() / 2}px)`}>
                  <S.TitleContentsShell
                    $isNoneDisplay={
                      checkEmptyRowValue(
                        financialTermsData.partnerA.totalDealValue,
                        financialTermsData.partnerB.totalDealValue,
                      ) || isPartnerBFinancialTermsTableValueEmpty
                    }
                  >
                    Total Payment
                  </S.TitleContentsShell>
                </S.BodyShell>
              </S.TableRow>

              <S.TableRow>
                <S.BodyShell width={`calc(50% - ${checkTableGap() / 2}px)`}>
                  <S.ValueContentsShell
                    $isNoneDisplay={
                      checkEmptyRowValue(
                        financialTermsData.partnerA.totalDealValue,
                        financialTermsData.partnerB.totalDealValue,
                      ) || isPartnerAFinancialTermsTableValueEmpty
                    }
                    $isBottomBorder={checkEmptyRowValue(
                      financialTermsData.partnerA.royalty,
                      financialTermsData.partnerB.royalty,
                    )}
                  >
                    {financialTermsData.partnerA.totalDealValue}
                  </S.ValueContentsShell>
                </S.BodyShell>
                <S.BodyShell width={`calc(50% - ${checkTableGap() / 2}px)`}>
                  <S.ValueContentsShell
                    $isNoneDisplay={
                      checkEmptyRowValue(
                        financialTermsData.partnerA.totalDealValue,
                        financialTermsData.partnerB.totalDealValue,
                      ) || isPartnerBFinancialTermsTableValueEmpty
                    }
                    $isBottomBorder={checkEmptyRowValue(
                      financialTermsData.partnerA.royalty,
                      financialTermsData.partnerB.royalty,
                    )}
                  >
                    {financialTermsData.partnerB.totalDealValue}
                  </S.ValueContentsShell>
                </S.BodyShell>
              </S.TableRow>

              <S.TableRow>
                <S.BodyShell width={`calc(50% - ${checkTableGap() / 2}px)`}>
                  <S.TitleContentsShell
                    $isNoneDisplay={
                      checkEmptyRowValue(
                        financialTermsData.partnerA.royalty,
                        financialTermsData.partnerB.royalty,
                      ) || isPartnerAFinancialTermsTableValueEmpty
                    }
                  >
                    Royalty
                  </S.TitleContentsShell>
                </S.BodyShell>
                <S.BodyShell width={`calc(50% - ${checkTableGap() / 2}px)`}>
                  <S.TitleContentsShell
                    $isNoneDisplay={
                      checkEmptyRowValue(
                        financialTermsData.partnerA.royalty,
                        financialTermsData.partnerB.royalty,
                      ) || isPartnerBFinancialTermsTableValueEmpty
                    }
                  >
                    Royalty
                  </S.TitleContentsShell>
                </S.BodyShell>
              </S.TableRow>

              <S.TableRow>
                <S.BodyShell width={`calc(50% - ${checkTableGap() / 2}px)`}>
                  <S.ValueContentsShell
                    $isBottomBorder
                    $isNoneDisplay={
                      checkEmptyRowValue(
                        financialTermsData.partnerA.royalty,
                        financialTermsData.partnerB.royalty,
                      ) || isPartnerAFinancialTermsTableValueEmpty
                    }
                  >
                    {financialTermsData.partnerA.royalty}
                  </S.ValueContentsShell>
                </S.BodyShell>
                <S.BodyShell width={`calc(50% - ${checkTableGap() / 2}px)`}>
                  <S.ValueContentsShell
                    $isBottomBorder
                    $isNoneDisplay={
                      checkEmptyRowValue(
                        financialTermsData.partnerA.royalty,
                        financialTermsData.partnerB.royalty,
                      ) || isPartnerBFinancialTermsTableValueEmpty
                    }
                  >
                    {financialTermsData.partnerB.royalty}
                  </S.ValueContentsShell>
                </S.BodyShell>
              </S.TableRow>
            </S.TableBody>
          </S.Table>
        </S.TableWrapper>
      </S.TableGroupWrapper>
    </ParagraphSectionLayout>
  );
};

export default DealTermsCrossLicenseTableContainer;
